import type { FC } from 'react'
import { Fragment } from 'react'
import dayjs from 'dayjs'
import { useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { getLocation } from '@jotta/grpc-connect-openapi/geoQuery'
import { useCurrentLanguage } from '@jotta/i18n'
import { Button } from '@jotta/ui/Button'
import { RelativeTime } from '@jotta/ui/RelativeTime'
import SvgX from '@jotta/ui/SvgX'
import { filesize } from '@jotta/utils/filesize'
import { formatWallClockUTCNanos } from '@jotta/utils/timestampUtils'

import type { CarouselPhoto } from '../../api/usePhoto'
import { formatVideoDuration } from '../PhotoGallery/timelinev2/PhotoRowV2'

export interface PhotoInfoProps {
  media: CarouselPhoto
  showOwnerName?: boolean
  onClose?: () => void
}

const getDetails = (
  {
    fileSize,
    width,
    height,
    videoDurationSeconds,
    ownerFullName,
  }: CarouselPhoto,
  showOwnerName?: boolean,
  location?: string,
): any[][] => [
  [t`File size`, filesize(fileSize)],
  [t`Dimension`, `${width} x ${height}`],
  [t`Location`, location],
  [
    t`Duration`,
    videoDurationSeconds
      ? formatVideoDuration(videoDurationSeconds)
      : undefined,
  ],
  [t`Added by`, showOwnerName && ownerFullName],
]

const getCameraSettings = ({
  camera,
  focalLength,
  exposure,
  iso,
}: CarouselPhoto): any[][] => [
  [t`Camera`, camera],
  [t`Focal length`, focalLength],
  [t`Exposure`, exposure],
  [`ISO`, iso],
]

function Columns({ data }: { data: any[][] }) {
  return (
    <div className="grid grid-cols-2 py-4">
      {data.map(([name, value]) =>
        value ? (
          <Fragment key={name}>
            <p className="opacity-60">{name}</p>
            <p>{value}</p>
          </Fragment>
        ) : undefined,
      )}
    </div>
  )
}

export const PhotoInfo: FC<PhotoInfoProps> = ({
  media,
  showOwnerName,
  onClose,
}) => {
  const { location, timestampNanos, fileName } = media
  const timestamp = dayjs
    .utc(timestampNanos / 1_000_000n)
    .add(new Date().getTimezoneOffset(), 'm')
  const formattedCaptureDate = formatWallClockUTCNanos(timestampNanos)
  const cameraSettings = getCameraSettings(media)
  const closeButtonHandler = () => {
    if (onClose) {
      onClose()
    }
  }

  const currentLanguage = useCurrentLanguage()
  const { data: geoAddress } = useQuery(
    getLocation,
    {
      location: location,
      language: currentLanguage,
    },
    {
      enabled: Boolean(location),
      throwOnError: false,
    },
  )
  const locationName = geoAddress?.cityName

  return (
    <div className="flex h-full flex-col bg-primary-fg px-6 py-2 text-white">
      {onClose && (
        <Button
          icon={SvgX}
          className="btn btn-ghost self-end text-white"
          onClick={closeButtonHandler}
        />
      )}
      <h2 className="label-lg pb-2 pt-4">{formattedCaptureDate}</h2>
      <h4 className="opacity-60">{fileName}</h4>
      <p className="opacity-60">
        <RelativeTime timestamp={timestamp} />
      </p>
      <Columns data={getDetails(media, showOwnerName, locationName)} />
      {/* map key invalidated, disable map until we have a proper alternative */}
      {location && Math.random() > 100 && (
        <img
          className="h-[240px]"
          src={`//maps.googleapis.com/maps/api/staticmap?markers=${location.latitude},${location.longitude}&zoom=13&scale=2&size=300x240&maptype=roadmap&format=jpg&visual_refresh=true&key=AIzaSyCD2pEVt8hMzhUi57udZ37BLm3iByghYw0`}
          alt="Geo Location"
        />
      )}
      {cameraSettings.some(e => !!e[1]) && (
        <h4 className="label-md pt-6 text-white opacity-60">
          <Trans>Camera settings</Trans>
        </h4>
      )}
      <Columns data={cameraSettings} />
    </div>
  )
}
